import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Offcanvas from "react-bootstrap/Offcanvas";
import LogoutModal from "../../Components/Modals/LogoutModal/LogoutModal";
// a plain PNG: the SVG export wrapped this picture in a pattern fill, which
// Safari sometimes painted as a black box (Devin 2026-10-29)
import logo from "../../Assets/logo-header.png";
import { useMaterialSymbols } from "../../Teacher/Oireachtas/components";
import "./ParentHeader.css";
import "./ParentNav.css";

// Signed-in parent/teacher navigation (Devin 2026-09-25): two places plus the
// profile. Events = the dashboard (your Entered cards + every event to browse);
// Results = results for your events, with All Results beside it. Desktop: tabs
// in the top bar with the profile icon and its menu at the right. Phones: the
// top bar is just the logo, and a bottom tab bar holds Events | Results |
// Profile (Profile opens the same menu as a bottom sheet). Pages with their own
// fixed action bar (the Oireachtas registration flow) hide the tab bar in CSS.

const Icon = ({ name }) => (
  <span className="msr" aria-hidden="true">
    {name}
  </span>
);

// The main section a path belongs to (for the active tab), or null.
export const sectionFor = (pathname) => {
  if (pathname === "/" || pathname === "/parent") return "events";
  if (/^\/(results|oireachtas\/results|oireachtas\/my-results)(\/|$)/.test(pathname)) return "results";
  if (pathname.startsWith("/my-account") || pathname.startsWith("/parent/transaction-history") || pathname.startsWith("/adjudicator/")) return "profile";
  return null;
};

const openDialog = () => !!document.querySelector(".modal.show");

const ProfileMenu = ({ onPick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const go = (to, tab) => () => {
    if (tab) dispatch({ type: "TAB_CHANGE", payload: tab });
    if (onPick) onPick();
    navigate(to);
  };
  return (
    <ul className="flh-menu" role="menu">
      <li>
        <button type="button" role="menuitem" onClick={go("/my-account", "1")}>
          <Icon name="person" />
          My account
        </button>
      </li>
      <li>
        <button type="button" role="menuitem" onClick={go("/my-account", "3")}>
          <Icon name="groups" />
          Dancers
        </button>
      </li>
      <li>
        <button type="button" role="menuitem" onClick={go("/parent/transaction-history")}>
          <Icon name="receipt_long" />
          Transaction history
        </button>
      </li>
      {localStorage.getItem("isAdjudicator") === "true" && (
        <li>
          <button type="button" role="menuitem" onClick={go("/adjudicator/profile")}>
            <Icon name="gavel" />
            Adjudicator profile
          </button>
        </li>
      )}
      <li className="flh-logout">
        <Icon name="logout" />
        <LogoutModal name="Logout" />
      </li>
    </ul>
  );
};

const ParentHeader = ({ deleted }) => {
  useMaterialSymbols();
  const { pathname } = useLocation();
  const section = sectionFor(pathname);
  const signedIn = !!localStorage.getItem("accessToken") && !deleted;
  const userName = localStorage.getItem("userFirstName") || "";
  const [menuOpen, setMenuOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const menuRef = useRef(null);

  // room for the phone tab bar at the bottom of every signed-in page
  useEffect(() => {
    if (!signedIn) return undefined;
    document.body.classList.add("fl-has-tabbar");
    return () => document.body.classList.remove("fl-has-tabbar");
  }, [signedIn]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      // dialogs opened from the menu (the logout confirmation) render in a
      // portal outside it; clicks there must not close the menu, which would
      // unmount the dialog before its button's click lands
      if (openDialog()) return;
      if (e.type === "keydown" ? e.key === "Escape" : !menuRef.current?.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  const tab = (key, to, icon, label) => (
    <Link to={to} className={`flh-tab${section === key ? " active" : ""}`} aria-current={section === key ? "page" : undefined}>
      <Icon name={icon} />
      <span>{label}</span>
    </Link>
  );

  return (
    <>
      <header className="flh">
        <a href="/" className="flh-logo">
          <img src={logo} alt="Feis.link" />
        </a>
        {signedIn && (
          <nav className="flh-tabs" aria-label="Main">
            {tab("events", "/parent", "event", "Events")}
            {tab("results", "/results", "emoji_events", "Results")}
            <div className="flh-profile" ref={menuRef}>
              <button
                type="button"
                className={`flh-profile-btn${section === "profile" ? " active" : ""}`}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={userName ? `${userName}'s account` : "Account"}
                onClick={() => setMenuOpen((o) => !o)}
              >
                <Icon name="account_circle" />
              </button>
              {menuOpen && (
                <div className="flh-pop">
                  {userName && <div className="flh-pop-name">{userName}</div>}
                  <ProfileMenu onPick={() => setMenuOpen(false)} />
                </div>
              )}
            </div>
          </nav>
        )}
      </header>

      {signedIn && (
        <nav className="flh-tabbar" aria-label="Main">
          {tab("events", "/parent", "event", "Events")}
          {tab("results", "/results", "emoji_events", "Results")}
          <button
            type="button"
            className={`flh-tab${section === "profile" || sheetOpen ? " active" : ""}`}
            onClick={() => setSheetOpen(true)}
          >
            <Icon name="account_circle" />
            <span>Profile</span>
          </button>
        </nav>
      )}

      <Offcanvas show={sheetOpen} onHide={() => setSheetOpen(false)} placement="bottom" className="flh-sheet">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{userName || "Account"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <ProfileMenu onPick={() => setSheetOpen(false)} />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default ParentHeader;
